import { Component, OnInit, ViewChild } from '@angular/core';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { DialPlan } from '../../_types/dial-plan';
import { DialPlansService, DialPlanTree, DialPlanActionNode } from '../../_services/dial-plans.service';
import { DialPlanFlowComponent } from '../dial-plan-flow/dial-plan-flow.component';

@Component({
    selector: 'app-dial-plans-detail',
    templateUrl: './dial-plans-detail.component.html',
    styleUrls: ['./dial-plans-detail.component.css'],
    standalone: false
})
export class DialPlansDetailComponent implements OnInit {
  public dialPlan: DialPlan = new DialPlan();
  public dialPlanFormGroup!: RxFormGroup;
  public rootActions: DialPlanActionNode[] = [];
  public loading = false;
  public saving = false;
  public isNew = true;

  @ViewChild(DialPlanFlowComponent) flow?: DialPlanFlowComponent;

  constructor(
    private dialPlansService: DialPlansService,
    private config: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private messageService: MessageService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService
  ) {
    this.dialPlanFormGroup = this.formBuilder.formGroup<DialPlan>(this.dialPlan) as RxFormGroup;
  }

  ngOnInit() {
    const id = this.config.data?.id;
    if (id) {
      this.isNew = false;
      this.loadTree(id);
    }
  }

  async loadTree(id: string) {
    this.loading = true;
    try {
      const tree: DialPlanTree = await firstValueFrom(this.dialPlansService.getTree(id));
      this.dialPlan = new DialPlan();
      this.dialPlan.id = tree.id;
      this.dialPlan.name = tree.name;
      this.dialPlanFormGroup.patchValue(this.dialPlan);
      this.rootActions = tree.actions;
    } finally {
      this.loading = false;
    }
  }

  async reload() {
    if (this.dialPlan.id) await this.loadTree(this.dialPlan.id);
  }

  save() {
    const valid = this.formValidationService.validateForm(this.dialPlanFormGroup);
    if (!valid) {
      document.getElementById('dialPlanForm')?.classList.add('formInvalid');
      return;
    }

    this.dialPlan = Object.assign(this.dialPlan, this.dialPlanFormGroup.value);
    this.saving = true;
    this.dialPlansService.save(this.dialPlan).subscribe({
      next: async (saved: any) => {
        try {
          if (this.flow) {
            await firstValueFrom(this.flow.commit());
          }
        } catch (e: any) {
          this.saving = false;
          this.messageService.add({ severity: 'error', detail: e?.error?.errors?.[0]?.title || e?.error?.title || 'Error al guardar el flujo' });
          return;
        }
        this.saving = false;
        this.messageService.add({ severity: 'success', detail: 'Plan guardado correctamente' });
        if (this.isNew) {
          this.dialPlan = saved;
          this.isNew = false;
        }
        // recargar para reflejar IDs reales tras crear nuevas acciones/ramas
        if (this.dialPlan.id) await this.loadTree(this.dialPlan.id);
      },
      error: (error: any) => {
        this.saving = false;
        this.messageService.add({ severity: 'error', detail: error?.error?.errors?.[0]?.title || error?.error?.title || 'Error al guardar el plan' });
      }
    });
  }

  cancel() {
    this.dynamicDialogRef.close();
  }
}
